import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowLeft, FileText, Mail, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsConditionsPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white py-8">
        <div className="container mx-auto px-4">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{language === 'hi' ? 'वापस जाएं' : 'Go Back'}</span>
          </button>
          <h1 className={`text-3xl md:text-4xl font-black ${language === 'hi' ? 'font-hindi' : ''}`}>
            {language === 'hi' ? 'नियम और शर्तें' : 'Terms & Conditions'}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-orange-100">
          
          {/* Introduction */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-8 h-8 text-orange-600" />
              <h2 className={`text-2xl font-bold text-gray-900 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? 'सेवा की शर्तें' : 'Terms of Service'}
              </h2>
            </div>
            <p className={`text-gray-600 leading-relaxed ${language === 'hi' ? 'font-hindi' : ''}`}>
              {language === 'hi'
                ? 'Karma Kundali की सेवाओं का उपयोग करके, आप निम्नलिखित नियमों और शर्तों से सहमत होते हैं। कृपया इन्हें ध्यान से पढ़ें।'
                : 'By using Karma Kundali services, you agree to the following terms and conditions. Please read them carefully.'}
            </p>
          </div>

          {/* Terms Points */}
          <div className="space-y-8">
            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '1. सेवा का विवरण' : '1. Service Description'}
              </h3>
              <ul className={`list-disc list-inside space-y-2 text-gray-600 ${language === 'hi' ? 'font-hindi' : ''}`}>
                <li>{language === 'hi' ? 'Karma Kundali वैदिक ज्योतिष पर आधारित प्रीमियम कुंडली रिपोर्ट प्रदान करती है।' : 'Karma Kundali provides premium Kundli reports based on Vedic astrology.'}</li>
                <li>{language === 'hi' ? 'रिपोर्ट 60+ पेज की होती है और 48 घंटे के भीतर ईमेल पर भेजी जाती है।' : 'Reports are 60+ pages and delivered via email within 48 hours.'}</li>
                <li>{language === 'hi' ? 'रिपोर्ट में जन्म कुंडली, दशा, विवाह, करियर, धन, स्वास्थ्य आदि का विश्लेषण शामिल है।' : 'Reports include analysis of birth chart, Dasha, marriage, career, wealth, health etc.'}</li>
              </ul>
            </div>

            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '2. उपयोगकर्ता की जिम्मेदारियां' : '2. User Responsibilities'}
              </h3>
              <ul className={`list-disc list-inside space-y-2 text-gray-600 ${language === 'hi' ? 'font-hindi' : ''}`}>
                <li>{language === 'hi' ? 'आप सही जन्म विवरण (नाम, तिथि, समय, स्थान) प्रदान करने के लिए जिम्मेदार हैं।' : 'You are responsible for providing accurate birth details (name, date, time, place).'}</li>
                <li>{language === 'hi' ? 'गलत जानकारी से गलत रिपोर्ट बन सकती है, जिसके लिए हम जिम्मेदार नहीं हैं।' : 'Incorrect information may result in inaccurate reports, for which we are not responsible.'}</li>
                <li>{language === 'hi' ? 'आप केवल व्यक्तिगत उपयोग के लिए रिपोर्ट का उपयोग करेंगे।' : 'You will use the report for personal use only.'}</li>
              </ul>
            </div>

            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '3. भुगतान की शर्तें' : '3. Payment Terms'}
              </h3>
              <ul className={`list-disc list-inside space-y-2 text-gray-600 ${language === 'hi' ? 'font-hindi' : ''}`}>
                <li>{language === 'hi' ? 'सभी भुगतान Razorpay के माध्यम से सुरक्षित रूप से किए जाते हैं।' : 'All payments are processed securely through Razorpay.'}</li>
                <li>{language === 'hi' ? 'वर्तमान मूल्य ₹99 है (कुंडली रिपोर्ट + निःशुल्क न्यूमेरोलॉजी बोनस)।' : 'Current price is ₹99 (Kundli report + Free Numerology bonus).'}</li>
                <li>{language === 'hi' ? 'भुगतान के बाद ऑर्डर कन्फर्मेशन ईमेल स्वचालित रूप से भेजी जाती है।' : 'Order confirmation email is sent automatically after payment.'}</li>
              </ul>
            </div>

            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '4. डिलीवरी' : '4. Delivery'}
              </h3>
              <ul className={`list-disc list-inside space-y-2 text-gray-600 ${language === 'hi' ? 'font-hindi' : ''}`}>
                <li>{language === 'hi' ? 'कुंडली रिपोर्ट 48 घंटे के भीतर आपकी ईमेल पर भेजी जाएगी।' : 'Kundli report will be sent to your email within 48 hours.'}</li>
                <li>{language === 'hi' ? 'रिपोर्ट PDF फॉर्मेट में होगी।' : 'Report will be in PDF format.'}</li>
                <li>{language === 'hi' ? 'कृपया अपना स्पैम फोल्डर भी चेक करें।' : 'Please check your spam folder as well.'}</li>
              </ul>
            </div>

            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '5. अस्वीकरण' : '5. Disclaimer'}
              </h3>
              <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
                <p className={`text-gray-700 ${language === 'hi' ? 'font-hindi' : ''}`}>
                  {language === 'hi'
                    ? 'कुंडली रिपोर्ट केवल मार्गदर्शन के लिए है। हम किसी भी निर्णय के परिणामों के लिए जिम्मेदार नहीं हैं। ज्योतिष विज्ञान है लेकिन 100% सटीकता की गारंटी नहीं दी जा सकती। महत्वपूर्ण निर्णयों के लिए कृपया विशेषज्ञों से परामर्श लें।'
                    : 'Kundli reports are for guidance only. We are not responsible for consequences of any decisions. Astrology is a science but 100% accuracy cannot be guaranteed. Please consult experts for important decisions.'}
                </p>
              </div>
            </div>

            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '6. बौद्धिक संपदा' : '6. Intellectual Property'}
              </h3>
              <ul className={`list-disc list-inside space-y-2 text-gray-600 ${language === 'hi' ? 'font-hindi' : ''}`}>
                <li>{language === 'hi' ? 'सभी रिपोर्ट और सामग्री Karma Kundali की संपत्ति है।' : 'All reports and content are property of Karma Kundali.'}</li>
                <li>{language === 'hi' ? 'रिपोर्ट को बेचना या पुनर्वितरित करना प्रतिबंधित है।' : 'Selling or redistributing reports is prohibited.'}</li>
              </ul>
            </div>

            <div>
              <h3 className={`text-xl font-bold text-gray-900 mb-3 ${language === 'hi' ? 'font-hindi' : ''}`}>
                {language === 'hi' ? '7. संपर्क' : '7. Contact'}
              </h3>
              <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                <p className={`text-gray-700 mb-4 ${language === 'hi' ? 'font-hindi' : ''}`}>
                  {language === 'hi' ? 'किसी भी प्रश्न के लिए:' : 'For any questions:'}
                </p>
                <div className="space-y-2">
                  <a href="mailto:karmakundali2@gmail.com" className="flex items-center gap-2 text-orange-600 hover:text-orange-700">
                    <Mail className="w-5 h-5" />
                    <span>karmakundali2@gmail.com</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Last Updated */}
          <div className="mt-10 pt-6 border-t border-gray-200">
            <p className="text-gray-500 text-sm">
              {language === 'hi' ? 'अंतिम अपडेट: फरवरी 2026' : 'Last Updated: February 2026'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditionsPage;
